import {
  useEffect, useState,
} from 'react'
import {
  settingsSectionFromSearch, type SettingsSectionId,
} from '../utils/settingsSections'

/** How long a linked section stays highlighted. */
export const SECTION_HIGHLIGHT_MS = 2500

/** Attribute that marks the linked section while it is highlighted; styled in index.css. */
export const SECTION_HIGHLIGHT_ATTRIBUTE = 'data-section-highlight'

/** How long to wait for a section that renders only after its data loads. */
const SECTION_WAIT_MS = 10_000

function requestedSection(search: string): SettingsSectionId | null {
  return settingsSectionFromSearch(new URLSearchParams(search))
}

// Most cards load their own data and render nothing until it arrives, and an
// add-on cell is `empty:hidden` until its card does, so a section counts as
// there once it has content, not merely once its element exists.
function renderedSection(id: SettingsSectionId): HTMLElement | null {
  const element = document.getElementById(id)
  return element && element.childElementCount > 0 ? element : null
}

function reveal(element: HTMLElement): () => void {
  element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  element.setAttribute(SECTION_HIGHLIGHT_ATTRIBUTE, '')
  const timer = window.setTimeout(
    () => element.removeAttribute(SECTION_HIGHLIGHT_ATTRIBUTE),
    SECTION_HIGHLIGHT_MS,
  )
  return () => window.clearTimeout(timer)
}

/**
 * Scroll to, and briefly highlight, the section a `?section=<id>` link names.
 *
 * The section is read at first render: the Settings page's own cleanup effects
 * strip the query string on mount, before most sections have loaded. A section
 * this build does not render, or one this account does not show, is waited for
 * and then given up on, leaving the visitor at the top of the page.
 */
export function useSettingsSectionDeepLink(): void {
  const [section] = useState(() => requestedSection(window.location.search))

  useEffect(() => {
    if (section === null) return
    let stopHighlight: (() => void) | undefined
    const found = renderedSection(section)
    if (found) return reveal(found)

    const observer = new MutationObserver(() => {
      const element = renderedSection(section)
      if (!element) return
      observer.disconnect()
      window.clearTimeout(giveUp)
      stopHighlight = reveal(element)
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const giveUp = window.setTimeout(() => observer.disconnect(), SECTION_WAIT_MS)
    return () => {
      observer.disconnect()
      window.clearTimeout(giveUp)
      stopHighlight?.()
    }
  }, [section])
}
